import { useContext, useState, useEffect } from 'react';
import { useMutation, UseMutationResult, useQuery } from '@tanstack/react-query';
import * as Sentry from '@sentry/react';
import toast from 'react-hot-toast';
import styled from 'styled-components';
import { StandardErrorToast } from 'design-system/src/components/Toast/Toast';
import { Typography } from 'design-system/src/components/Typography/Typography';
import Button from 'design-system/src/components/Button/Button';
import ButtonRow, { Alignment } from 'design-system/src/components/ButtonRow/ButtonRow';
import Hairline from 'design-system/src/components/Hairline/Hairline';
import { RenderHtmlString } from 'design-system/src/components/RenderHtmlString/RenderHtmlString';
import {
  client,
  getRxFilePreviewUrl,
  TaskComment,
  TaskEvent,
  OutboundTaskTypeKey,
  OutboundTaskType,
} from '../../../../api';
import { RadioLabel, RadioOptionWrapper, RadioBlockGroup } from 'design-system/src/components/Radio/Radio';
import { TaskContext } from '../../contexts';
import LmnRespondedModal from '../../LmnRespondedModal/LmnRespondedModal';
import { FormActionContainer, StickyButtonContainer, StyledIframe } from '../../actions';
import { TaskInstructionSummary, TaskCompletionEventBanner } from '../../shared';
import { StructuredTaskViewWrapper } from '..';
import LetterOfMedicalNecessityEditModal from '../../../LmnEditModal';
import { FloatingEditButton } from '../../../FloatingEditButton/FloatingEditButton';
import DeclineModal from '../../DeclineModal';
import NewFollowupTaskModal from '../../NewFollowupTaskModal';
import { OtherRequestButton } from '../../actions/shared/OtherRequestButton';
import type { LetterOfMedicalNecessityTask, WithAdditionalTaskResponseFields } from '../../types';

const StyledFormActionContainer = styled(FormActionContainer)`
  padding-bottom: 0;
`;

type BlockingLmnResponse = {
  blocking_lmn_id: string;
  lmn_id: string;
  document_id: string;
  editable_letter_content: string;
  status: string;
  type: string;
};

const FLOATING_BUTTON_TOP_OFFSET = '-90px';
const FLOATING_BUTTON_RIGHT_OFFSET = '32px';

const LMN_RADIO_OPTIONS: { value: ResponseType; label: string }[] = [
  { value: 'approve', label: 'Approve Letter of Medical Necessity and submit PA' },
  { value: 'decline', label: 'Decline Letter of Medical Necessity and submit PA' },
];

interface InProgressViewProps {
  documentId: string;
  rxId: string;
  setResponseType: (responseType: ResponseType) => void;
  responseType: ResponseType;
  submit: UseMutationResult<void, Error, ResponseType, unknown>;
  blockingLmnId: string | undefined;
  onEditClick: () => void;
  onCancelPrescription: () => void;
  setShowNewFollowupTaskModal: (show: boolean) => void;
}
const InProgressView = ({
  documentId,
  rxId,
  setResponseType,
  responseType,
  submit,
  blockingLmnId,
  onEditClick,
  onCancelPrescription,
  setShowNewFollowupTaskModal,
}: InProgressViewProps) => {
  return (
    <>
      <StyledFormActionContainer>
        {documentId && <StyledIframe src={getRxFilePreviewUrl(rxId, documentId)} />}

        <StickyButtonContainer>
          {blockingLmnId && (
            <FloatingEditButton
              top={FLOATING_BUTTON_TOP_OFFSET}
              right={FLOATING_BUTTON_RIGHT_OFFSET}
              onClick={onEditClick}
            />
          )}
          <RadioBlockGroup $variant="outlined">
            {LMN_RADIO_OPTIONS.map((option) => (
              <RadioOptionWrapper
                id={option.value}
                name="lmn-response"
                value={option.value}
                checked={responseType === option.value}
                onChange={() => setResponseType(option.value)}
                key={option.value}
                variant="outlined"
                disabled={submit.isPending}
              >
                <RadioLabel>{option.label}</RadioLabel>
              </RadioOptionWrapper>
            ))}
          </RadioBlockGroup>
          <ButtonRow align={Alignment.SpaceBetween}>
            <ButtonRow>
              <Button size="sm" variant="tertiary" onClick={onCancelPrescription} disabled={submit.isPending}>
                Cancel prescription
              </Button>
              <OtherRequestButton setShowNewFollowupTaskModal={setShowNewFollowupTaskModal} />
            </ButtonRow>
            <Button size="sm" onClick={() => submit.mutate(responseType)} isLoading={submit.isPending}>
              Submit
            </Button>
          </ButtonRow>
        </StickyButtonContainer>
      </StyledFormActionContainer>
    </>
  );
};

type ResponseType = 'approve' | 'decline';

/**
 * Version of TaskView that is used for LMN (Review Letter of Medical Necessity) tasks.
 */
const LetterOfMedicalNecessityTaskView: React.FC<{
  taskResponse: WithAdditionalTaskResponseFields<LetterOfMedicalNecessityTask>;
}> = ({ taskResponse }) => {
  const [showRespondedModal, setShowRespondedModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeclineModal, setShowDeclineModal] = useState(false);
  const [showNewFollowupTaskModal, setShowNewFollowupTaskModal] = useState(false);
  const [responseType, setResponseType] = useState<ResponseType>('approve');
  const [currentDocumentId, setCurrentDocumentId] = useState<string>((taskResponse.task as any).document_id ?? '');
  const [editableContent, setEditableContent] = useState<string>('');
  const { taskId, onTaskUpdate } = useContext(TaskContext);
  const isTrainingMode = import.meta.env.VITE_TRAINING_MODE === 'true';

  const { drug, patient, prescriber } = taskResponse;
  const task = taskResponse.task;

  // Get blocking_lmn_id from task response
  const blockingLmnId = (task as any).blocking_lmn_id;

  // Fetch blocking LMN data for editing (only needed to get editable_letter_content)
  const { data: blockingLmnData } = useQuery<BlockingLmnResponse>({
    queryKey: ['lmn-task', blockingLmnId],
    queryFn: async () => {
      const response = await client.get(`lmn-task/${blockingLmnId}`);
      return response.data;
    },
    enabled: !!blockingLmnId,
  });

  // Initialize document ID and editable content from blocking LMN data on first load
  useEffect(() => {
    if (blockingLmnData?.document_id && !currentDocumentId) {
      setCurrentDocumentId(blockingLmnData.document_id);
    }
    if (blockingLmnData?.editable_letter_content && !editableContent) {
      setEditableContent(blockingLmnData.editable_letter_content);
    }
  }, [blockingLmnData, editableContent, currentDocumentId]);

  const submit = useMutation<void, Error, ResponseType>({
    mutationFn: async (responseType: ResponseType) => {
      if (isTrainingMode) {
        console.warn(`Training Mode: Mocking "submit LMN response" for task ID: ${taskId}`);
        return;
      }
      const formData = new FormData();
      formData.append('task_response', responseType === 'approve' ? 'approve_lmn' : 'decline_lmn');
      const res = await client.post(`tasks/${taskId}/respond`, formData);
      return res.data;
    },
    onSuccess: () => {
      onTaskUpdate();
      setShowRespondedModal(true);
    },
    onError: (error) => {
      toast.custom(() => <StandardErrorToast />);
      console.error(error);
      Sentry.captureException(error);
    },
  });

  const handleEditClick = () => {
    if (blockingLmnData) {
      setShowEditModal(true);
    }
  };

  const isCompleted = task.status === 'completed';

  const initialComment =
    task.timeline && task.timeline.length > 0 && task.timeline[0].item_type === 'comment'
      ? (task.timeline[0] as TaskComment)
      : undefined;

  // Log if we have an open task without initial comment
  if (!isCompleted && !initialComment) {
    Sentry.captureMessage('LMN task missing initial comment', {
      level: 'error',
      extra: { taskId },
    });
  }

  // Use document_id - initially from task response, then updated from blocking LMN data via useEffect
  const documentId = currentDocumentId;

  // Get completion event for completed tasks
  let completionEvent: TaskEvent | undefined;
  if (isCompleted) {
    if (task.timeline && task.timeline.length >= 2) {
      const secondTimelineItem = task.timeline[1];
      if (secondTimelineItem?.item_type === 'event') {
        completionEvent = secondTimelineItem as TaskEvent;
      }
    }
    if (!completionEvent) {
      Sentry.captureMessage('No completion event found for LMN task', {
        level: 'error',
        extra: {
          taskId,
        },
      });
    }
  }

  const responseModal = (
    <LmnRespondedModal
      closeModal={() => setShowRespondedModal(false)}
      patient={patient}
      drug={drug}
      variant={responseType}
    />
  );

  const body = (
    <StructuredTaskViewWrapper
      taskType={task.type as OutboundTaskType}
      generalInfoTableProps={{
        patientName: drug.patient_name,
        prescriberName: drug.prescriber_name,
        dob: drug.dob,
        prescribedOn: drug.prescription_recieved_at,
        drugName: drug.name,
        prescriptionId: drug.id,
      }}
      taskCreatedAt={task.created_at}
      isCompleted={isCompleted}
      showBusinessRelationshipSmallPrint
      sidebar={{
        drug: {
          data: drug,
          showBusinessRelationshipNotice: true,
        },
        patient: patient ?? undefined,
        prescriber: prescriber ?? undefined,
      }}
    >
      {isCompleted && <TaskCompletionEventBanner completionEvent={completionEvent} />}

      <div className="mb-4 flex flex-col gap-3">
        <TaskInstructionSummary>
          Please review the letter and select an option below within 3 days to proceed.
        </TaskInstructionSummary>
        {initialComment && (
          <Typography styledAs="bodyExtraSmallDMSans">
            <RenderHtmlString>{initialComment.message}</RenderHtmlString>
          </Typography>
        )}
      </div>
      <Hairline />

      {!isCompleted && initialComment && (
        <InProgressView
          documentId={documentId}
          rxId={drug.id}
          setResponseType={setResponseType}
          responseType={responseType}
          submit={submit}
          blockingLmnId={blockingLmnId}
          onEditClick={handleEditClick}
          onCancelPrescription={() => setShowDeclineModal(true)}
          setShowNewFollowupTaskModal={setShowNewFollowupTaskModal}
        />
      )}
    </StructuredTaskViewWrapper>
  );

  return (
    <>
      {body}

      {showRespondedModal && responseType && responseModal}

      {showDeclineModal && (
        <DeclineModal
          closeModal={() => setShowDeclineModal(false)}
          patient={patient}
          drug={drug}
          taskKey={OutboundTaskTypeKey.LETTER_OF_MEDICAL_NECESSITY}
          taskId={taskId}
          onTaskUpdate={onTaskUpdate}
        />
      )}

      {showNewFollowupTaskModal && (
        <NewFollowupTaskModal
          closeModal={() => setShowNewFollowupTaskModal(false)}
          patient={patient}
          drug={drug}
          patientId={patient.id}
          prescriptionId={drug.id}
          taskId={taskId}
          relatedTaskKey={OutboundTaskTypeKey.LETTER_OF_MEDICAL_NECESSITY}
          relatedTaskType={task.type as OutboundTaskType}
        />
      )}

      {showEditModal && blockingLmnId && blockingLmnData && (
        <LetterOfMedicalNecessityEditModal
          context="blocking_lmn"
          blockingLmnId={blockingLmnId}
          lmnId={blockingLmnData.lmn_id}
          letterContent={editableContent}
          closeModal={() => setShowEditModal(false)}
          setDocumentId={setCurrentDocumentId}
          setEditableContent={setEditableContent}
        />
      )}
    </>
  );
};

export default LetterOfMedicalNecessityTaskView;
